import { createGlobalStyle } from 'styled-components';

export type ButtonShadowKey =
  | 'tertiaryDefault'
  | 'tertiaryHover'
  | 'tertiaryActive'
  | 'tertiaryDisabled'
  | 'secondaryDefault'
  | 'secondaryHover'
  | 'secondaryActive'
  | 'primaryDefault'
  | 'primaryHover'
  | 'primaryActive'
  | 'dangerDefault'
  | 'dangerHover'
  | 'dangerActive'
  | 'secondaryDangerDefault'
  | 'secondaryDangerHover'
  | 'secondaryDangerActive'
  | 'secondaryDangerDisabled';

const cssVariableName = (key: ButtonShadowKey): string =>
  `--button-shadow-${key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}`;

export const buttonShadow = (key: ButtonShadowKey): string =>
  `var(${cssVariableName(key)})`;

export const buttonShadowLightTokens: Record<ButtonShadowKey, string> = {
  tertiaryDefault: `0px 1px 1px 0px rgba(0, 0, 0, 0.02), 0px -2px 2px 0px rgba(211, 211, 211, 0.25) inset`,
  tertiaryHover: `0px 1px 1px 0px rgba(0, 0, 0, 0.02), 0px -2px 2px 0px rgba(255, 255, 255, 0.40) inset`,
  tertiaryActive: `0px 0px 0px 2px rgba(255, 255, 255, 0.25) inset`,
  tertiaryDisabled: `0px 0px 0px 2px rgba(255, 255, 255, 0.25) inset`,
  secondaryDefault: `0px 2px 1px 0px rgba(226, 194, 255, 0.15), 0px -2px 2px 0px rgba(255, 255, 255, 0.20) inset, 0px 0px 0px 2px rgba(255, 255, 255, 0.16) inset`,
  secondaryHover: `0px 2px 1px 0px rgba(226, 194, 255, 0.15), 0px -2px 2px 0px rgba(255, 255, 255, 0.40) inset, 0px 0px 0px 2px rgba(255, 255, 255, 0.05) inset`,
  secondaryActive: `0px 0px 0px 2px rgba(255, 255, 255, 0.05) inset`,
  primaryDefault: `0px -2px 2px 0px #1F003B inset, 0px 0px 0px 2px rgba(255, 255, 255, 0.11) inset, 0px 2px 1px 0px rgba(46, 0, 87, 0.05)`,
  primaryHover: `0px -2px 2px 0px rgba(255, 255, 255, 0.40) inset, 0px 0px 0px 2px rgba(255, 255, 255, 0.11) inset, 0px 2px 1px 0px rgba(46, 0, 87, 0.05)`,
  primaryActive: `0px 0px 0px 2px rgba(255, 255, 255, 0.11) inset`,
  dangerDefault: `0px 2px 1px 0px rgba(246, 178, 178, 0.10), 0px -2px 2px 0px rgba(255, 255, 255, 0.20) inset, 0px 0px 0px 2px rgba(255, 255, 255, 0.16) inset`,
  dangerHover: `0px -2px 2px 0px rgba(255, 255, 255, 0.20) inset, 0px 0px 0px 2px rgba(255, 255, 255, 0.05) inset, 0px 2px 1px 0px rgba(246, 178, 178, 0.15)`,
  dangerActive: `0px 0px 0px 2px rgba(255, 255, 255, 0.05) inset`,
  secondaryDangerDefault: `0px 1px 1px 0px rgba(0, 0, 0, 0.02), 0px -2px 2px 0px rgba(211, 211, 211, 0.25) inset`,
  secondaryDangerHover: `0px 1px 1px 0px rgba(0, 0, 0, 0.02), 0px -2px 2px 0px rgba(255, 255, 255, 0.40) inset`,
  secondaryDangerActive: `0px 0px 0px 2px rgba(255, 255, 255, 0.25) inset`,
  secondaryDangerDisabled: `0px 0px 0px 2px rgba(255, 255, 255, 0.25) inset`,
};

const RING = '0 0 0 1px var(--border-subtle)';

export const buttonShadowDarkTokens: Record<ButtonShadowKey, string> = {
  tertiaryDefault: `${RING}, 0px 1px 2px rgba(0, 0, 0, 0.5)`,
  tertiaryHover: `${RING}, 0px 1px 2px rgba(0, 0, 0, 0.5)`,
  tertiaryActive: RING,
  tertiaryDisabled: RING,
  secondaryDefault: `${RING}, 0px 2px 6px rgba(0, 0, 0, 0.55)`,
  secondaryHover: `${RING}, 0px 2px 6px rgba(0, 0, 0, 0.55)`,
  secondaryActive: RING,
  primaryDefault: `${RING}, 0px 2px 6px rgba(0, 0, 0, 0.55)`,
  primaryHover: `${RING}, 0px 2px 6px rgba(0, 0, 0, 0.55)`,
  primaryActive: RING,
  dangerDefault: `${RING}, 0px 2px 6px rgba(0, 0, 0, 0.55)`,
  dangerHover: `${RING}, 0px 2px 6px rgba(0, 0, 0, 0.55)`,
  dangerActive: RING,
  secondaryDangerDefault: `${RING}, 0px 1px 2px rgba(0, 0, 0, 0.5)`,
  secondaryDangerHover: `${RING}, 0px 1px 2px rgba(0, 0, 0, 0.5)`,
  secondaryDangerActive: RING,
  secondaryDangerDisabled: RING,
};

const declareVariables = (tokens: Record<ButtonShadowKey, string>): string =>
  (Object.keys(tokens) as ButtonShadowKey[])
    .map(key => `${cssVariableName(key)}: ${tokens[key]};`)
    .join('\n');

export const ButtonShadowVariables = createGlobalStyle`
  :root {
    ${declareVariables(buttonShadowLightTokens)}
  }

  .dark-mode {
    ${declareVariables(buttonShadowDarkTokens)}
  }
`;
